import { Box, Typography, Grid, IconButton, Divider } from "@mui/material";
import { useTranslation } from "react-i18next";

import LocationOnIcon from "@mui/icons-material/LocationOn";
import PhoneIcon from "@mui/icons-material/Phone";
import EmailIcon from "@mui/icons-material/Email";

import FacebookIcon from "@mui/icons-material/Facebook";
import XIcon from "@mui/icons-material/X";
import InstagramIcon from "@mui/icons-material/Instagram";
import MusicNoteIcon from "@mui/icons-material/MusicNote"; // TikTok substitute
import contactimg from "../images/pexels-alex-andrews-271121-821754.jpg";
import NavBar from "../NavBar/NavBar";
import { useEffect, useRef, useState } from "react";
import SEO from "../app/SEO";

export default function ContactPage() {
  const { t, i18n } = useTranslation("contact");
  const [lightNav, setLightNav] = useState(true);
  const heroRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        setLightNav(entry.isIntersecting);
      },
      { threshold: 0.05 }
    );

    if (heroRef.current) observer.observe(heroRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <Box sx={{ backgroundColor: "#F6F5F3", minHeight: "100vh" }}>
      <SEO
        title="Contact Helvetia | Talent Representation, Bookings & Fan Experiences"
        description="Get in touch with Helvetia to discuss talent representation, celebrity bookings, private events, brand collaborations, or exclusive fan experiences. Our team responds with discretion, professionalism, and care."
        canonical={`https://helvieta.com/${i18n.language}/contact`}
        ogImage={contactimg}
      />
      <NavBar mode={lightNav ? "light" : "dark"} />
      {/* HERO */}
      <Box
        sx={{
          position: "relative",
          height: { xs: "45vh", md: "60vh" },
          backgroundColor: "#0B0B0B",
          overflow: "hidden",
        }}
      >
        <Box
          ref={heroRef}
          sx={{
            position: "absolute",
            inset: 0,
            backgroundImage: `url(${contactimg})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            filter: "brightness(0.6)",
          }}
        />

        <Box
          sx={{
            position: "absolute",
            bottom: { xs: 40, md: 80 },
            left: { xs: 24, md: 80 },
          }}
        >
          <Typography
            sx={{
              fontFamily: "Playfair Display",
              fontSize: { xs: "2.5rem", md: "4.5rem" },
              color: "#FFFFFF",
              fontWeight: 700,
              letterSpacing: "0.05em",
              textTransform: "uppercase",
            }}
          >
            {t("title")}
          </Typography>
        </Box>
      </Box>

      {/* CONTENT */}
      <Box
        sx={{
          maxWidth: 1200,
          mx: "auto",
          px: { xs: 3, md: 8 },
          py: { xs: 8, md: 12 },
        }}
      >
        <Grid container spacing={8}>
          {/* ADDRESSES */}
          <Grid item xs={12} md={4}>
            <Typography sx={sectionTitle}>
              <LocationOnIcon sx={iconStyle} /> Offices
            </Typography>

            {["la", "london", "capeTown", "hongKong"].map((key) => (
              <Box key={key} sx={{ mb: 3 }}>
                <Typography sx={labelStyle}>
                  {t(`addresses.${key}.label`)}
                </Typography>
                <Typography sx={bodyStyle}>
                  {t(`addresses.${key}.address`)}
                </Typography>
              </Box>
            ))}
          </Grid>

          {/* PHONES */}
          <Grid item xs={12} md={4}>
            <Typography sx={sectionTitle}>
              <PhoneIcon sx={iconStyle} /> Phone
            </Typography>

            {["la", "london", "capeTown", "hongKong"].map((key) => (
              <Typography key={key} sx={bodyStyle} mb={2}>
                {t(`phones.${key}`)}
              </Typography>
            ))}
          </Grid>

          {/* EMAILS */}
          <Grid item xs={12} md={4}>
            <Typography sx={sectionTitle}>
              <EmailIcon sx={iconStyle} /> Email
            </Typography>

            {["inquiries", "bookings", "talent", "dancing"].map((key) => (
              <Typography
                key={key}
                component="a"
                href={`mailto:${t(`emails.${key}`)}`}
                sx={{
                  ...bodyStyle,
                  display: "block",
                  mb: 2,
                  textDecoration: "none",
                  "&:hover": { textDecoration: "underline" },
                }}
              >
                {t(`emails.${key}`)}
              </Typography>
            ))}
          </Grid>
        </Grid>

        <Divider sx={{ my: 8 }} />

        {/* SOCIALS */}
        <Box sx={{ textAlign: "center" }}>
          {[FacebookIcon, XIcon, InstagramIcon, MusicNoteIcon].map(
            (Icon, index) => (
              <IconButton
                key={index}
                sx={{
                  mx: 1,
                  color: "#111",
                  transition: "transform 0.3s ease",
                  "&:hover": {
                    transform: "translateY(-3px)",
                  },
                }}
              >
                <Icon />
              </IconButton>
            )
          )}
        </Box>
      </Box>
    </Box>
  );
}

/* STYLES */
const sectionTitle = {
  fontFamily: "Playfair Display",
  fontSize: "1.4rem",
  fontWeight: 600,
  mb: 3,
  display: "flex",
  alignItems: "center",
  gap: 1,
};

const labelStyle = {
  fontFamily: "Inter",
  fontSize: "0.95rem",
  fontWeight: 600,
  color: "#111",
};

const bodyStyle = {
  fontFamily: "Inter",
  fontSize: "0.95rem",
  lineHeight: 1.7,
  color: "#444",
};

const iconStyle = {
  fontSize: "1.3rem",
  color: "#111",
};
